// Платформенная панель: метрики (MRR/ARR), компании-клиенты, тарифы, блокировка.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building, Users, TrendingUp, Ban, Search, Check } from 'lucide-react';
import { platformApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/format';
import type { PlatformCompany } from '@/types';

const PLAN_COLOR: Record<string, string> = {
  trial: 'var(--text-muted)', basic: 'var(--blue)',
  pro: 'var(--accent)', enterprise: 'var(--purple, #7b70f0)',
};
const PLANS = [
  { value: 'trial', label: 'Пробный' },
  { value: 'basic', label: 'Базовый' },
  { value: 'pro', label: 'Профессиональный' },
  { value: 'enterprise', label: 'Корпоративный' },
];

function money(v: number) {
  return new Intl.NumberFormat('ru-RU').format(v) + ' ₽';
}

export function PlatformAdminPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');

  const { data: stats } = useQuery({ queryKey: ['platform-stats'], queryFn: () => platformApi.stats() });
  const { data: companies, isLoading } = useQuery({
    queryKey: ['platform-companies', { search, planFilter }],
    queryFn: () => platformApi.companies({
      ...(search ? { search } : {}), ...(planFilter ? { plan: planFilter } : {}),
    }),
  });

  const planM = useMutation({
    mutationFn: ({ id, plan }: { id: number; plan: string }) => platformApi.setPlan(id, plan),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform-companies'] }); qc.invalidateQueries({ queryKey: ['platform-stats'] }); toast('Тариф изменён', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });
  const blockM = useMutation({
    mutationFn: (id: number) => platformApi.toggleActive(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform-companies'] }); qc.invalidateQueries({ queryKey: ['platform-stats'] }); toast('Статус компании изменён', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });

  // Доступ только платформенным админам
  if (!user?.is_platform_admin) {
    return <AppLayout><EmptyState icon={<Ban size={32} />} title="Доступ запрещён"
      hint="Эта панель доступна только администраторам платформы" /></AppLayout>;
  }

  return (
    <AppLayout>
      <PageHeader title="Платформенная панель" subtitle="Управление компаниями-клиентами Struxis" />

      {/* Метрики */}
      {stats && (
        <div className="kpi-grid" style={{ marginBottom: 24 }}>
          <MetricCard icon={<Building size={20} />} label="Компаний" value={String(stats.total_companies)}
            hint={`${stats.active_companies} активных · ${stats.blocked_companies} заблок.`} />
          <MetricCard icon={<Users size={20} />} label="Пользователей" value={String(stats.total_users)}
            hint={`${stats.trial_companies} на пробном`} />
          <MetricCard icon={<TrendingUp size={20} />} label="MRR" value={money(stats.mrr)}
            hint="ежемесячная выручка" accent />
          <MetricCard icon={<TrendingUp size={20} />} label="ARR" value={money(stats.arr)}
            hint="годовая выручка" />
        </div>
      )}

      {/* Разбивка по тарифам */}
      {stats && (
        <div className="panel" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 14, fontSize: 15 }}>Выручка по тарифам</h3>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {stats.by_plan.map((p) => (
              <div key={p.plan} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: PLAN_COLOR[p.plan], marginRight: 6 }} />
                  {p.label}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 18 }}>{p.count}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{money(p.revenue)}/мес</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="field__input" style={{ paddingLeft: 36 }} value={search}
            onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по названию или ИНН…" />
        </div>
        <select className="field__select" style={{ maxWidth: 220 }} value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
          <option value="">Все тарифы</option>
          {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>

      {/* Компании */}
      {isLoading ? <Spinner label="Загрузка компаний…" /> :
        !companies || companies.length === 0 ? <EmptyState title="Компаний нет" /> : (
          <div className="panel">
            <table className="data-table">
              <thead>
                <tr><th>Компания</th><th>ИНН</th><th>Сотр.</th><th>Тариф</th><th style={{ textAlign: 'right' }}>₽/мес</th><th>Статус</th><th>Регистрация</th><th></th></tr>
              </thead>
              <tbody>
                {companies.map((c: PlatformCompany) => (
                  <tr key={c.id} style={{ opacity: c.is_active ? 1 : 0.5 }}>
                    <td style={{ fontWeight: 500 }}>{c.name}</td>
                    <td className="mono">{c.inn}</td>
                    <td className="mono">{c.members_count}</td>
                    <td>
                      <select value={c.plan} onChange={(e) => planM.mutate({ id: c.id, plan: e.target.value })}
                        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: 6, color: PLAN_COLOR[c.plan], padding: '4px 8px', fontSize: 13 }}>
                        {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </td>
                    <td style={{ textAlign: 'right' }} className="mono">{money(c.monthly_price)}</td>
                    <td>
                      {c.is_active
                        ? <StatusBadge label="Активна" color="var(--green)" />
                        : <StatusBadge label="Заблокирована" color="var(--red)" />}
                    </td>
                    <td className="mono">{formatDate(c.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button onClick={() => blockM.mutate(c.id)} title={c.is_active ? 'Заблокировать' : 'Разблокировать'}
                        style={{ background: 'transparent', border: '1px solid var(--border-bright)', borderRadius: 6, color: c.is_active ? 'var(--red)' : 'var(--green)', padding: '5px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        {c.is_active ? <><Ban size={13} /> Блок</> : <><Check size={13} /> Разблок</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </AppLayout>
  );
}

function MetricCard({ icon, label, value, hint, accent }: {
  icon: React.ReactNode; label: string; value: string; hint?: string; accent?: boolean;
}) {
  return (
    <div className="kpi-card" style={accent ? { borderColor: 'var(--accent)' } : undefined}>
      <div className="kpi-card__icon" style={accent ? { color: 'var(--accent)' } : undefined}>{icon}</div>
      <div className="kpi-card__value">{value}</div>
      <div className="kpi-card__label">{label}</div>
      {hint && <div className="kpi-card__hint">{hint}</div>}
    </div>
  );
}
